import {useCallback, useMemo, useSyncExternalStore} from 'react'

import {useBoardConfig, useBoardDataContext} from '../provider/contexts'
import type {CellAddress} from '../types'
import {type BoardCardDescriber, createCardDescriber} from './card-detail'
import {useBoardSelectionStore} from './contexts'
import {selectionKey} from './next-selection'
import type {BoardSelectedCard} from './types'

export type UseBoardSelectionResult = {
  /** Exactly what the caller stated, in the order it stated it. */
  selectedCards: readonly BoardSelectedCard[]
  /** How many cards are selected. */
  count: number
  /** Whether one card is selected, answered in constant time. */
  isSelected: (card: BoardSelectedCard) => boolean
  /**
   * The selected cards in one cell, in the order the caller stated them.
   *
   * A selection never spans two cells, so this is either the whole selection or none of
   * it. It is here so a caller can ask the question without knowing that.
   */
  cardsIn: (address: CellAddress) => readonly BoardSelectedCard[]
  /**
   * What one card is, in the words a reader knows: its column, its row, the cell's own
   * name, the card's own name, and the caller's item.
   *
   * A call rather than a second array beside `selectedCards`, so a caller drawing a count
   * pays nothing for labels it never reads, and one drawing the whole selection writes
   * `selectedCards.map(card => describeCard(card))` and pays only then.
   *
   * A column, a row and a cell are each a lookup in an index the provider already built,
   * so a caller never writes `columns.find(...)` over an axis or `cells.find(...)` over a
   * board. The one index the provider builds lazily -- which item in a cell carries which
   * id -- is filled on the first card resolved in that cell and is linear in that cell's
   * loaded cards alone; every card resolved in that cell afterwards is O(1). A selection
   * lives in one cell, so a selection of any size fills at most one, and only its first
   * card pays for it.
   *
   * Every part of the answer is optional and absent means absent: a card whose item has
   * been moved or paged away is reported without one rather than with its raw id dressed
   * up as a name.
   */
  describeCard: BoardCardDescriber
  /** Selects nothing. */
  clear: () => void
  /**
   * Selects every card the caller has **loaded** into one cell, replacing whatever was
   * selected before.
   *
   * Loaded, not every card the cell has. A cell states a `totalCount` that can be far
   * larger than the page it holds, and the ids of the cards nobody has fetched do not
   * exist yet on this client. Selecting what is not there would mean inventing it, which
   * is the one thing this contract never does.
   */
  selectAllIn: (address: CellAddress) => void
}

/**
 * The whole selection, for something that acts on it or draws it: a toolbar, a count, a
 * bulk control, a panel naming what is selected.
 *
 * **Not for a card.** This re-renders whenever the selection changes at all, which is
 * what a toolbar wants and the opposite of what a card wants. A card asks `useBoardCard`
 * for its own `isSelected`, which is a boolean subscription that wakes it only when its
 * own answer changed. Reaching for this inside a card would put every card on the board
 * back to re-rendering on every gesture, which is the cost the selection store exists to
 * avoid. `describeCard` reads the board's data as well, so a card calling this would also
 * re-render on every fetch.
 *
 * Everything it hands back is the caller's own selection read back, the caller's own data
 * read back, or a way of handing the caller a new selection. The board still holds
 * nothing and derives nothing.
 */
export function useBoardSelection(): UseBoardSelectionResult {
  const {getSelectedCards, subscribe, selectCards, cellItemIds} = useBoardSelectionStore()
  const data = useBoardDataContext()
  const config = useBoardConfig()

  // The array the caller stated, whose identity changes only when the caller changes it,
  // so this is a snapshot `useSyncExternalStore` can compare.
  const selectedCards = useSyncExternalStore(subscribe, getSelectedCards, getSelectedCards)

  // Rebuilt when the selection changes and not on every question asked of it, so a
  // toolbar drawing a control per card does not turn each one into a scan.
  const selectedKeys = useMemo(() => new Set(selectedCards.map(selectionKey)), [selectedCards])

  const isSelected = useCallback((card: BoardSelectedCard) => selectedKeys.has(selectionKey(card)), [selectedKeys])

  const cardsIn = useCallback(
    ({columnKey, rowKey}: CellAddress) =>
      selectedCards.filter(card => card.columnKey === columnKey && card.rowKey === rowKey),
    [selectedCards],
  )

  const clear = useCallback(() => selectCards(NOTHING_SELECTED), [selectCards])

  // New whenever the labels or accessors behind an answer change, so a resolution is
  // always in the words the caller states now. The index it reads is the provider's and
  // outlives it: a config is rebuilt whenever any accessor or label the caller states
  // changes identity, which happens for reasons that say nothing about which item carries
  // which id, and rebuilding the index for those would put the one scan this surface
  // makes back on the interaction path.
  const describeCard = useMemo(() => createCardDescriber({data, config}), [data, config])

  const selectAllIn = useCallback(
    (address: CellAddress) => selectCards(cellItemIds(address).map(itemId => ({...address, itemId}))),
    [selectCards, cellItemIds],
  )

  return {selectedCards, count: selectedCards.length, isSelected, cardsIn, describeCard, clear, selectAllIn}
}

const NOTHING_SELECTED: readonly BoardSelectedCard[] = []
